<template>
  <v-dialog
    v-model="modal"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
  >
    <div class="camera-wrapper">
      <div class="preview">
        <video ref="video" muted autoplay playsinline></video>
      </div>

      <div class="controls">
        <div @click="closeCamera()" class="close-camera">
          <v-icon color="white" size="30">mdi-arrow-left</v-icon>
        </div>
        <div class="tabs" v-if="!(activeTab === 'video' && isRecording)">
          <button
            class="tab"
            :class="{ active: activeTab === 'photo' }"
            @click="activeTab = 'photo'"
          >
            FOTO
          </button>
          <button
            v-if="!(component === 'entry_medias' || component === 'leaving_medias')"
            class="tab"
            :class="{ active: activeTab === 'video' }"
            @click="activeTab = 'video'"
          >
            VÍDEO
          </button>
        </div>

        <div class="shutter">
          <div
            class="progress-container"
            v-if="activeTab === 'video' && isRecording"
          >
            <div class="progress-ring" :style="progressStyle"></div>
          </div>

          <button
            class="shutter-button"
            :class="{ recording: activeTab === 'video' && isRecording }"
            @click="handleShutterClick"
          >
            <span v-if="activeTab === 'video' && isRecording" class="timer-inside">
              {{ Math.floor(recordingTime) }}s
            </span>
          </button>
        </div>
      </div>
    </div>

    <v-dialog v-model="showConfirmDialog" persistent max-width="300">
      <v-card>
        <v-card-text style="padding: 24px;">
          O vídeo atingiu 2 minutos. Clique em confirmar para finalizar.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="confirmStopRecording">
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
export default {
  name: "CameraComponent",
  props: {
    component: String,
  },
  data() {
    return {
      modal: true,
      activeTab: "photo",
      isRecording: false,
      stream: null,
      videoResolution: null,
      recordedChunks: [],
      mediaRecorder: null,
      selectedMimeType: null,
      showConfirmDialog: false,
      recordingTimeout: null,
      recordingInterval: null,
      recordingTime: 0,
      autoStopped: false,
      recordedBlob: null,
    };
  },
  computed: {
    progressStyle() {
      const progress = Math.min(this.recordingTime / 120, 1) * 360;
      return {
        background: `conic-gradient(white ${progress}deg, transparent ${progress}deg)`,
      };
    },
  },
  mounted() {
    this.startCamera();
  },
  methods: {
    async startCamera() {
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
        video: {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          aspectRatio: { ideal: 16 / 9 },
          frameRate: { ideal: 60 },
        },
      };

      try {
        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
        const videoEl = this.$refs.video;
        if (videoEl) {
          videoEl.srcObject = this.stream;
          videoEl.onloadedmetadata = () => {
            this.videoResolution = {
              width: videoEl.videoWidth,
              height: videoEl.videoHeight,
            };
          };
        }
      } catch (error) {
        console.error("Erro ao acessar a câmera:", error);
      }
    },
    handleShutterClick() {
      if (this.activeTab === "photo") {
        this.capturePhoto();
      } else if (this.activeTab === "video") {
        if (this.isRecording) {
          this.autoStopped = false;
          this.stopRecording();
        } else {
          this.startRecording();
        }
      }
    },
    capturePhoto() {
      const videoEl = this.$refs.video;
      if (!videoEl) return;

      const canvas = document.createElement("canvas");
      canvas.width = videoEl.videoWidth;
      canvas.height = videoEl.videoHeight;
      const context = canvas.getContext("2d");
      context.drawImage(videoEl, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        if (blob) {
          this.$emit("media-captured", { type: "photo", blob });
          this.stopCamera();
          this.modal = false;
        }
      }, "image/png");
    },
    startRecording() {
      if (!this.stream) {
        console.error("Stream não disponível para gravação.");
        return;
      }

      this.recordedChunks = [];
      this.autoStopped = false;
      this.recordingTime = 0;

      let mimeType = "video/mp4; codecs=avc1.64001E,mp4a.40.2";
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        console.warn(
          `${mimeType} não é suportado. Usando fallback para video/webm`
        );
        mimeType = "video/webm; codecs=vp8";
      }
      this.selectedMimeType = mimeType;
      const options = { mimeType };

      try {
        this.mediaRecorder = new MediaRecorder(this.stream, options);
      } catch (err) {
        console.error("Erro ao criar MediaRecorder:", err);
        return;
      }

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = this.handleStopRecording;

      this.mediaRecorder.start();
      this.isRecording = true;

      this.recordingTimeout = setTimeout(() => {
        if (this.isRecording) {
          this.autoStopped = true;
          this.stopRecording();
        }
      }, 120000);

      this.recordingInterval = setInterval(() => {
        this.recordingTime += 0.1;
      }, 100);
    },
    stopRecording() {
      if (this.mediaRecorder && this.isRecording) {
        clearTimeout(this.recordingTimeout);
        clearInterval(this.recordingInterval);
        this.mediaRecorder.stop();
      }
    },
    handleStopRecording() {
      const blob = new Blob(this.recordedChunks, { type: this.selectedMimeType });
      if (this.autoStopped) {
        this.recordedBlob = blob;
        this.isRecording = false;
        this.showConfirmDialog = true;
      } else {
        this.$emit("media-captured", { type: "video", blob });
        this.stopCamera();
        this.isRecording = false;
        this.modal = false;
      }
    },
    confirmStopRecording() {
      this.$emit("media-captured", { type: "video", blob: this.recordedBlob });
      this.stopCamera();
      this.modal = false;
      this.showConfirmDialog = false;
    },
    closeCamera() {
      this.stopCamera();
      this.$emit("close");
    },
    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
      }
      if (this.recordingInterval) {
        clearInterval(this.recordingInterval);
      }
      if (this.recordingTimeout) {
        clearTimeout(this.recordingTimeout);
      }
    }
  },
  beforeUnmount() {
    this.stopCamera();
  },
};
</script>

<style scoped>
.camera-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #000;
  overflow: hidden;
}

/* Área de preview da câmera */
.preview {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

video {
  width: 100%;
}

/* Controles inferiores */
.controls {
  position: absolute;
  bottom: 20px;
  width: 100%;
  text-align: center;
}

.close-camera {
  position: fixed;
  bottom: 40px;
  left: 34px;
  background-color: #000;
  padding: 5px;
  border-radius: 50%;
}

.tabs {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.tab {
  font-size: 18px;
  color: #fff;
  background: transparent;
  border: none;
  margin: 0 20px;
  padding: 10px 0;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: border-color 0.3s;
}

.tab.active {
  border-bottom: 2px solid #fff;
}

.shutter {
  position: relative;
  display: inline-block;
  margin: 0 auto;
}

.shutter-button {
  position: relative;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: 5px solid #fff;
  background: #fff;
  cursor: pointer;
  outline: none;
  transition: background 0.3s, border-color 0.3s;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
}

.shutter-button.recording {
  background: red;
  border-color: darkred;
}

.progress-container {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 80px;
  height: 80px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  z-index: 1;
}

.progress-ring {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.timer-inside {
  color: #fff;
}
</style>
